import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

export interface RowAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
  reason?: string;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  title?: string;
}

/**
 * Agrupa las acciones secundarias de una fila en un menú desplegable, para
 * evitar filas con una tira larga de íconos.
 */
const RowActionsMenu = ({
  actions,
  title = 'Más acciones',
}: RowActionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const visibleActions = actions.filter((action) => !action.hidden);

  if (visibleActions.length === 0) return null;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (action: RowAction) => {
    handleClose();
    action.onClick();
  };

  return (
    <>
      <IconButton onClick={handleOpen} title={title} size='small'>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {visibleActions.map((action) => (
          <span
            key={action.key}
            title={action.disabled ? action.reason : undefined}
          >
            <MenuItem
              onClick={() => handleSelect(action)}
              disabled={action.disabled}
              dense
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          </span>
        ))}
      </Menu>
    </>
  );
};

export default RowActionsMenu;
